import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Ensure axios sends/receives cookies for auth everywhere
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined); // undefined = still checking
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data.user);
      return res.data.user;
    } catch (e) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from the OAuth callback, skip the /me check —
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (email, password, name) => {
    let utm = null;
    try {
      const raw = typeof localStorage !== "undefined" ? localStorage.getItem("passadc_utm") : null;
      if (raw) utm = JSON.parse(raw);
    } catch { /* noop */ }
    const res = await axios.post(`${API}/auth/register`, { email, password, name, utm });
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {}
    setUser(null);
  };

  // Detect "signed in elsewhere" globally
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 401 && typeof detail === "string" && detail.includes("another device")) {
          setUser(null);
          toast.error("Signed in on another device", {
            description: "For your security, only one device can be logged in at a time.",
          });
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const isPro = (() => {
    if (!user) return false;
    if (user.role === "admin") return true;
    const st = user.subscription_status;
    const cancelledStates = st === "cancelled" || st === "canceled";
    if (st !== "active" && st !== "trialing" && !cancelledStates) return false;
    if (user.pro_until && new Date(user.pro_until) <= new Date()) return false;
    if (cancelledStates && !user.pro_until) return false; // auto-renew off: paid period still counts
    return true;
  })();

  const isTrialing = !!user && user.subscription_status === "trialing" && !!user.pro_until && new Date(user.pro_until) > new Date();
  const trialDaysLeft = isTrialing ? Math.max(1, Math.ceil((new Date(user.pro_until) - new Date()) / 86400000)) : 0;
  const trialEnded = !!user && user.subscription_status === "trialing" && !!user.pro_until && new Date(user.pro_until) <= new Date();

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, isPro, isTrialing, trialDaysLeft, trialEnded }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};

// Error formatter for API responses (handles FastAPI's array-of-objects 422 responses)
export const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail === "object") return detail.message || JSON.stringify(detail);
  return String(detail);
};
